import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Clock,
  Phone,
  Mail,
  Calendar,
  AlertCircle,
  Eye,
  FileCheck,
  MessageSquare,
  RefreshCw,
  Send,
  Download,
  Share2,
  Heart,
  Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { SeeItInActionSection } from '../components/home/SeeItInActionSection';

// The Invisible Wall — Marquee Section
// Hero Section
const HeroSection = () => (
  <section className="relative pt-28 md:pt-36 pb-12 md:pb-16 bg-[#FAFAF9]" data-testid="hero-section">
    <div className="max-w-[950px] mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center">
        {/* Eyebrow - no background, bold */}
        <p 
          className="text-xs md:text-sm font-bold tracking-[0.12em] uppercase text-[#880808] mb-6 opacity-0 animate-[fadeSlideUp_0.6s_ease-out_0.1s_forwards] motion-reduce:opacity-100 motion-reduce:animate-none" 
          data-testid="hero-eyebrow"
        >
          INFRASTRUCTURE FOR SOLO OPERATORS
        </p>
        
        {/* H1 - charcoal grey */}
        <h1 
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-[#36454F] tracking-tight leading-[1.05] mb-5 opacity-0 animate-[fadeSlideUp_0.6s_ease-out_0.2s_forwards] motion-reduce:opacity-100 motion-reduce:animate-none" 
          data-testid="hero-headline"
        >
          RUN YOUR BUSINESS ON A SYSTEM,<br />
          NOT A STRING OF TEXT MESSAGES.
        </h1>
        
        {/* Subheading */}
        <p 
          className="text-base md:text-lg text-[#57534E] leading-relaxed max-w-2xl mx-auto mb-6 opacity-0 animate-[fadeSlideUp_0.6s_ease-out_0.3s_forwards] motion-reduce:opacity-100 motion-reduce:animate-none" 
          data-testid="hero-subhead"
        >
          Servitium gives solo and micro-businesses verified identity, a structured job workflow, in-app payments, and a track record that follows you everywhere — so every job adds to what you've built, instead of disappearing into a thread.
        </p>
        
        {/* Primary CTA */}
        <div className="opacity-0 animate-[fadeSlideUp_0.6s_ease-out_0.4s_forwards] motion-reduce:opacity-100 motion-reduce:animate-none mb-4">
          <Link to="/get" data-testid="hero-cta-primary">
            <Button className="bg-[#880808] hover:bg-[#6b0606] text-white rounded-full px-10 py-5 text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105">
              Join free
            </Button>
          </Link>
        </div>
        
        {/* Microcopy line */}
        <p 
          className="text-[13px] text-[#6b7280] mb-6 opacity-0 animate-[fadeSlideUp_0.6s_ease-out_0.5s_forwards] motion-reduce:opacity-100 motion-reduce:animate-none"
          data-testid="hero-microcopy"
        >
          Free across the Lower Mainland · Supported by Futurpreneur Canada & BDC
        </p>
        
        {/* Badge Cards */}
        <div 
          className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 opacity-0 animate-[fadeSlideUp_0.6s_ease-out_0.6s_forwards] motion-reduce:opacity-100 motion-reduce:animate-none"
          data-testid="hero-badges"
        >
          {/* Futurpreneur Badge */}
          <a
            href="https://futurpreneur.ca/en/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Futurpreneur Canada (opens in new tab)"
            className="block w-full sm:w-auto max-w-[340px] sm:max-w-none"
            data-testid="badge-futurpreneur"
          >
            <div className="bg-white rounded-xl p-4 md:p-5 shadow-[0_2px_12px_rgba(0,0,0,0.06)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.1)] hover:-translate-y-1 transition-all duration-200 ease-out cursor-pointer w-full sm:w-[240px] md:w-[260px] h-[88px] md:h-[100px] flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/efnyjxv1_FP%20card.png" 
                alt="Futurpreneur Canada" 
                className="max-h-full max-w-full object-contain"
              />
            </div>
          </a>
          
          {/* BDC Badge */}
          <a
            href="https://www.bdc.ca/en?utm_source=google&utm_medium=cpc&utm_campaign=brand-core_lead_sem_google_en_all_can&utm_term=bdc&utm_id=22860846897&utm_source_platform=Google+Ads&utm_content=1_Brand_Core&gclsrc=aw.ds&gad_source=1&gad_campaignid=22860846897&gbraid=0AAAAAD1PdqM9bWGnDlFgtc3IsKj56_9TJ&gclid=CjwKCAiAssfLBhBDEiwAcLpwftI4UuYSSTKTKurZynf8YD4V3imCT9mPSMpLlXnryxMAnMc15E9txxoCKMYQAvD_BwE"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="BDC (opens in new tab)"
            className="block w-full sm:w-auto max-w-[340px] sm:max-w-none"
            data-testid="badge-bdc"
          >
            <div className="bg-white rounded-xl p-4 md:p-5 shadow-[0_2px_12px_rgba(0,0,0,0.06)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.1)] hover:-translate-y-1 transition-all duration-200 ease-out cursor-pointer w-full sm:w-[240px] md:w-[260px] h-[88px] md:h-[100px] flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/5vqtbwia_BDC%20card.png" 
                alt="BDC - Business Development Bank of Canada" 
                className="max-h-full max-w-full object-contain"
              />
            </div>
          </a>
        </div>
      </div>
    </div>
  </section>
);

// Problem Section (3 bullets max)
const ProblemSection = () => (
  <section className="py-20 md:py-28 bg-[#F5F5F4]" data-testid="problem-section">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#1C1917] tracking-tight leading-tight mb-4" data-testid="problem-headline">
          The real cost? <span className="text-[#880808]">Confusion.</span>
        </h2>
      </div>
      
      <div className="grid md:grid-cols-3 gap-6">
        {[
          {
            icon: Clock,
            title: 'Endless follow-ups',
            description: 'Hours lost chasing responses'
          },
          {
            icon: MessageSquare,
            title: '"Any updates?"',
            description: 'The question that never stops'
          },
          {
            icon: AlertCircle,
            title: 'Trust erodes',
            description: 'Silence breeds doubt'
          }
        ].map((item, index) => (
          <div
            key={index}
            className="group p-6 md:p-8 rounded-2xl bg-white border border-[#E7E5E4] shadow-sm hover:shadow-md transition-all duration-300"
            data-testid={`problem-item-${index}`}
          >
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <item.icon className="w-6 h-6 text-red-600" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-bold text-[#1C1917] mb-1">{item.title}</h3>
            <p className="text-sm text-[#57534E]">{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Solution Section (3 icon tiles)
// Infrastructure Section (THE INFRASTRUCTURE)
const InfrastructureSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const supportingCards = [
    {
      title: 'CUSTOMERS COME TO YOU.',
      body: 'Quote requests, calls, email, and in-app messages all come from people actively looking to hire. No cold outreach, no follow-up texts, no chasing.'
    },
    {
      title: 'THREE APPS BECOME ONE.',
      body: 'Booking, payments, and communication run on a single workflow — so jobs stop slipping between texts, screenshots, and tabs.'
    },
    {
      title: 'YOUR RECORD IS YOURS.',
      body: 'Take your verified track record anywhere — share it in a referral, a message, or a card. It doesn\'t live inside someone else\'s review platform.'
    },
    {
      title: 'COMPETE ON QUALITY, NOT PRICE.',
      body: 'When customers can see what you\'ve actually delivered, they choose based on proof — not the lowest bid.'
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-20 md:py-28 bg-[#FAFAF9] relative overflow-hidden" 
      data-testid="infrastructure-section"
      id="the-infrastructure"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-gradient-to-r from-[#880808]/[0.02] via-[#FAB515]/[0.03] to-[#880808]/[0.02] rounded-full blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Eyebrow */}
        <div className="text-center mb-12">
          <p className="text-xs md:text-sm font-bold tracking-[0.15em] uppercase text-[#880808]" data-testid="infrastructure-eyebrow">
            THE INFRASTRUCTURE
          </p>
        </div>

        {/* Hero Card */}
        <div
          className={`
            bg-white rounded-2xl overflow-hidden mb-8
            shadow-[0_4px_24px_rgba(0,0,0,0.08)]
            transition-all duration-500 ease-out
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
          `}
          data-testid="infrastructure-hero-card"
        >
          <div className="h-[5px] bg-[#880808] w-full" />
          <div className="p-8 md:p-12 lg:p-14 text-center">
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#36454F] tracking-tight leading-tight mb-4" data-testid="infrastructure-hero-title">
              YOUR REPUTATION COMPOUNDS.
            </h3>
            <p className="text-base md:text-lg text-[#57534E] leading-relaxed max-w-3xl mx-auto">
              Every completed job becomes a verified entry on a track record only you can build. The longer you work, the harder you are to replace.
            </p>
          </div>
        </div>

        {/* 2x2 Supporting Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {supportingCards.map((card, index) => (
            <div
              key={index}
              className={`
                group bg-white rounded-2xl overflow-hidden
                shadow-[0_4px_20px_rgba(0,0,0,0.06)]
                hover:shadow-[0_8px_30px_rgba(0,0,0,0.1)]
                hover:-translate-y-1
                transition-all duration-200 ease-out
                flex flex-col
                motion-reduce:transition-none motion-reduce:hover:transform-none
                ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}
              `}
              style={{
                transitionDelay: isVisible ? `${(index + 1) * 100}ms` : '0ms',
                transitionProperty: 'opacity, transform, box-shadow'
              }}
              data-testid={`infrastructure-card-${index}`}
            >
              <div className="h-[4px] bg-[#D4AF37] w-full" />
              <div className="p-6 md:p-8 flex flex-col flex-grow text-center">
                <h3 className="text-lg md:text-xl font-extrabold text-[#1C1917] tracking-tight leading-tight mb-3">
                  {card.title}
                </h3>
                <p className="text-[#57534E] text-sm md:text-base leading-relaxed flex-grow">
                  {card.body}
                </p>
              </div>
              <div className="h-[3px] bg-[#E7E5E4] w-full" />
            </div>
          ))}
        </div>

        {/* Bottom Hero Card */}
        <div
          className={`
            bg-white rounded-2xl overflow-hidden mt-8
            shadow-[0_4px_24px_rgba(0,0,0,0.08)]
            transition-all duration-500 ease-out
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
          `}
          style={{ transitionDelay: isVisible ? '500ms' : '0ms' }}
          data-testid="infrastructure-bottom-card"
        >
          <div className="h-[5px] bg-[#880808] w-full" />
          <div className="p-8 md:p-12 lg:p-14 text-center">
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#36454F] tracking-tight leading-tight mb-4">
              CUSTOMERS GET REAL-TIME UPDATES.
            </h3>
            <p className="text-base md:text-lg text-[#57534E] leading-relaxed max-w-3xl mx-auto">
              Customers see live job status, next steps, and payment confirmations in their app. You don't have to send a thing.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// How It Works Section (iPhone Mockup + 4 Cards — Synced)
const HowItWorksSection = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const autoPlayRef = useRef(null);
  const resumeTimerRef = useRef(null);

  const SCREENSHOTS = [
    'https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/oydgcxmq_1.PNG',
    'https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/gsnnqtnu_Screenshot%202026-02-08%20at%202.10.23%E2%80%AFPM.png',
    'https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/u9ec8gc7_3.PNG',
  ];

  const steps = [
    { step: 1, title: 'Verify', body: 'Set up your business profile and verify your identity. You\'re live in minutes.' },
    { step: 2, title: 'Get Found', body: 'Quote requests, calls, email, and in-app messages come in from customers actively looking to hire. You respond.' },
    { step: 3, title: 'Deliver', body: 'Run the job, message, and get paid — all in one workflow. Every completed job adds a verified entry to your track record.' },
  ];

  // Detect reduced motion
  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mql.matches);
    const h = (e) => setReducedMotion(e.matches);
    mql.addEventListener('change', h);
    return () => mql.removeEventListener('change', h);
  }, []);

  // Auto-advance every 3s
  useEffect(() => {
    if (isPaused || reducedMotion) {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      return;
    }
    autoPlayRef.current = setInterval(() => {
      setActiveStep(prev => (prev + 1) % 3);
    }, 3000);
    return () => { if (autoPlayRef.current) clearInterval(autoPlayRef.current); };
  }, [isPaused, reducedMotion]);

  // User interaction: set step + pause + resume after 5s
  const goToStep = (idx) => {
    setActiveStep(idx);
    setIsPaused(true);
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => setIsPaused(false), 5000);
  };

  // Cleanup resume timer
  useEffect(() => {
    return () => { if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current); };
  }, []);

  const handleHoverEnter = () => setIsPaused(true);
  const handleHoverLeave = () => {
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => setIsPaused(false), 5000);
  };

  return (
    <section className="py-20 md:py-28 bg-white" data-testid="how-it-works-section" id="how-it-works">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-xs md:text-sm font-bold tracking-[0.22em] uppercase text-[#880808] mb-4">
            HOW IT WORKS
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* LEFT — iPhone Mockup */}
          <div
            className="flex justify-center lg:justify-end order-2 lg:order-1"
            onMouseEnter={handleHoverEnter}
            onMouseLeave={handleHoverLeave}
          >
            <div className="relative">
              {/* iPhone 16 Pro Max Frame */}
              <div className="relative w-[280px] md:w-[300px] h-[570px] md:h-[610px] bg-[#1C1917] rounded-[50px] p-[10px] shadow-2xl">
                {/* Dynamic Island */}
                <div className="absolute top-[18px] left-1/2 -translate-x-1/2 w-[100px] h-[32px] bg-black rounded-full z-20" />

                {/* Screen Area — crossfade between 4 real screenshots */}
                <div className="relative w-full h-full bg-[#FAFAF9] rounded-[42px] overflow-hidden">
                  {SCREENSHOTS.map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt={`Step ${i + 1} screenshot`}
                      className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
                      style={{ opacity: activeStep === i ? 1 : 0 }}
                      draggable={false}
                    />
                  ))}
                </div>

                {/* Home Indicator */}
                <div className="absolute bottom-[8px] left-1/2 -translate-x-1/2 w-[120px] h-[4px] bg-white/30 rounded-full" />
              </div>

              {/* Dots under phone — 4 dots */}
              <div className="flex items-center justify-center gap-4 mt-6">
                <button
                  onClick={() => goToStep((activeStep + 2) % 3)}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                  data-testid="mockup-prev"
                >
                  <ChevronLeft className="w-4 h-4 text-[#36454F]" />
                </button>

                <div className="flex gap-2">
                  {[0, 1, 2].map((i) => (
                    <button
                      key={i}
                      onClick={() => goToStep(i)}
                      className={`h-2.5 rounded-full transition-all duration-300 ${
                        activeStep === i
                          ? 'bg-[#880808] w-6'
                          : 'bg-gray-300 hover:bg-gray-400 w-2.5'
                      }`}
                      data-testid={`mockup-dot-${i}`}
                    />
                  ))}
                </div>

                <button
                  onClick={() => goToStep((activeStep + 1) % 3)}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                  data-testid="mockup-next"
                >
                  <ChevronRight className="w-4 h-4 text-[#36454F]" />
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT — 4 Stacked Cards */}
          <div
            className="flex flex-col gap-4 order-1 lg:order-2"
            onMouseEnter={handleHoverEnter}
            onMouseLeave={handleHoverLeave}
          >
            {steps.map((step, index) => {
              const isActive = activeStep === index;
              return (
                <div
                  key={index}
                  onClick={() => goToStep(index)}
                  className={`
                    bg-white rounded-xl overflow-hidden
                    border border-[#E7E5E4]
                    transition-all duration-200 ease-out
                    cursor-pointer
                    flex
                    hover:-translate-y-0.5
                    motion-reduce:hover:transform-none motion-reduce:transition-none
                    ${isActive ? 'shadow-[0_4px_20px_rgba(0,0,0,0.08)]' : 'shadow-[0_2px_8px_rgba(0,0,0,0.04)]'}
                  `}
                  data-testid={`how-step-${index}`}
                >
                  {/* Left accent bar */}
                  <div className={`w-[5px] flex-shrink-0 transition-colors duration-200 ${isActive ? 'bg-[#880808]' : 'bg-transparent'}`} />

                  <div className="p-5 md:p-6 flex-1">
                    <div className="flex items-baseline gap-3 mb-2">
                      <span className="text-xs font-extrabold text-[#880808] uppercase tracking-wide whitespace-nowrap">
                        STEP {step.step}
                      </span>
                      <h3 className="text-base md:text-lg font-extrabold text-[#1C1917] uppercase tracking-tight leading-tight">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-sm md:text-base text-[#6b7280] leading-relaxed">
                      {step.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};


// Pricing Section
const PricingSection = () => {
  const [citiesOpen, setCitiesOpen] = useState(false);

  const cities = [
    'Anmore', 'Belcarra', 'Bowen Island', 'Burnaby', 'Coquitlam',
    'Delta', 'Langley City', 'Langley Township', 'Lions Bay', 'Maple Ridge',
    'New Westminster', 'North Vancouver', 'Pitt Meadows', 'Port Coquitlam',
    'Port Moody', 'Richmond', 'Surrey', 'Vancouver', 'West Vancouver', 'White Rock',
  ];

  return (
    <section className="py-20 md:py-28 bg-white" data-testid="pricing-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Eyebrow */}
        <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#880808] mb-4" data-testid="pricing-eyebrow">
          PRICING
        </p>

        {/* Headline */}
        <h2
          className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#36454F] tracking-tight leading-tight mb-4"
          data-testid="pricing-headline"
        >
          FREE ACROSS THE LOWER MAINLAND.
        </h2>

        {/* Expandable city list toggle */}
        <button
          onClick={() => setCitiesOpen(!citiesOpen)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#880808] hover:text-[#6b0606] transition-colors mb-6"
          data-testid="pricing-cities-toggle"
        >
          {citiesOpen ? 'Hide cities' : 'See Lower Mainland cities (free)'}
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-200 ${citiesOpen ? 'rotate-180' : ''}`}
            strokeWidth={2.5}
          />
        </button>

        {/* Expandable city list panel */}
        <div
          className="overflow-hidden transition-all duration-300 ease-out"
          style={{ maxHeight: citiesOpen ? 400 : 0, opacity: citiesOpen ? 1 : 0 }}
        >
          <div className="bg-[#F8F6F0] rounded-xl p-6 md:p-8 mb-6 text-left" data-testid="pricing-cities-panel">
            <p className="text-xs font-bold tracking-[0.12em] uppercase text-[#880808] mb-4">
              Free for businesses operating in:
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5">
              {cities.map((city) => (
                <li key={city} className="text-sm text-[#57534E]">{city}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Body paragraph */}
        <p className="text-base md:text-lg text-[#57534E] leading-relaxed max-w-2xl mx-auto mb-8">
          Verified profile, structured job workflow, in-app payments, and a track record that follows you. No platform fee. No commission. No success fee. Forever.
        </p>

        {/* CTA + Store badges */}
        <div className="flex flex-col items-center gap-4 mb-6">
          <Link to="/get" data-testid="pricing-cta">
            <Button className="bg-[#880808] hover:bg-[#6b0606] text-white rounded-full px-10 py-5 text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105">
              Join free
            </Button>
          </Link>
          <div className="flex items-center gap-3" data-testid="pricing-store-badges">
            <a href="#" aria-label="Download on the App Store" data-testid="pricing-appstore">
              <img src="/apple-badge.svg" alt="App Store" className="h-10 opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
            </a>
            <a href="#" aria-label="Get it on Google Play" data-testid="pricing-googleplay">
              <img src="/google-badge.svg" alt="Google Play" className="h-10 opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
            </a>
          </div>
        </div>

        {/* Stripe disclosure */}
        <p className="text-xs italic text-[#6b7280] mb-3 max-w-lg mx-auto" data-testid="pricing-stripe-disclosure">
          Stripe payment processing (2.99% + 30¢) applies to completed transactions. Stripe charges this directly — Servitium takes no cut.
        </p>

        {/* Outer-city line */}
        <p className="text-[11px] text-[#9ca3af] max-w-md mx-auto" data-testid="pricing-outer-city">
          Operating outside the Lower Mainland? Servitium is $25/month. Your city is checked at signup.
        </p>
      </div>
    </section>
  );
};

// Final CTA Section (Talk to Team lives here)
const FinalCTASection = () => {
  const [showContact, setShowContact] = useState(false);

  return (
    <section className="py-20 md:py-28 bg-[#880808]" data-testid="final-cta-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight mb-4" data-testid="final-cta-headline">
          Ready to build trust?
        </h2>
        <p className="text-white/80 mb-8">
          Join business owners who stopped chasing and started connecting.
        </p>
        
        <div className="flex flex-col items-center gap-4">
          <Link to="/get" data-testid="final-cta-primary">
            <Button className="bg-white hover:bg-stone-100 text-[#880808] rounded-full px-10 py-5 text-lg font-bold shadow-lg transition-all duration-200 hover:scale-105">
              Get The App
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          
          <button
            onClick={() => setShowContact(!showContact)}
            className="text-white/70 hover:text-white text-sm font-medium underline underline-offset-4 transition-colors"
            data-testid="talk-to-team-button"
          >
            Talk to our Team
          </button>
        </div>

        {/* Contact iframe (only in this section) */}
        {showContact && (
          <div className="mt-8 rounded-2xl overflow-hidden shadow-2xl bg-white" data-testid="contact-iframe-container">
            <iframe
              src="https://servitium.ca/new-connect-with-us"
              title="Talk to our Team"
              className="w-full min-h-[500px] border-0"
              data-testid="contact-iframe"
            />
          </div>
        )}
      </div>
    </section>
  );
};

// Main HomePage Component
const HomePage = () => {
  return (
    <main data-testid="home-page">
      <HeroSection />
      <InfrastructureSection />
      <HowItWorksSection />
      <SeeItInActionSection />
      <PricingSection />
    </main>
  );
};

export default HomePage;
